// Facet filtering + ask-similarity ranking for the similar-variant picker.
// A family's variants differ almost entirely on brand · origin · state, so
// those three are the essential narrowing dimensions; everything is computed
// client-side from the already-loaded variant list (no facet endpoint).

export type FacetKey = 'brand' | 'origin' | 'state';

// Structural subset of MasterProduct — enough for facet math, and what the
// tests construct.
export interface FacetSource {
  brand?: string | null;
  origin?: string | null;
  state?: string | null;
}

export interface FacetValue {
  value: string;
  count: number;
}

const norm = (s?: string | null): string => (s ?? '').trim();

// Distinct non-empty values of one facet with usage counts — most common
// first so the likeliest chip sits leftmost, ties alphabetical for stability.
export function facetValues(variants: FacetSource[], key: FacetKey): FacetValue[] {
  const counts = new Map<string, number>();
  variants.forEach((variant) => {
    const value = norm(variant[key]);
    if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

// Selected facets combine with AND. A variant with no value on a selected
// facet is excluded — the operator asked for "origin: Bahrain", and unknown
// is not Bahrain.
export function filterVariants<T extends FacetSource>(
  variants: T[],
  selection: Partial<Record<FacetKey, string>>
): T[] {
  const active = (Object.entries(selection) as Array<[FacetKey, string | undefined]>).filter(([, value]) => !!value);
  if (!active.length) return variants;
  return variants.filter((variant) => active.every(([key, value]) => norm(variant[key]) === value));
}

// Rank by closeness to the requested item: state is the strongest signal
// (chilled-vs-frozen changes the product), then origin, then brand — weights
// 4/2/1 make the order lexicographic. Stable: ties keep the incoming order.
export function similaritySort<T extends FacetSource>(variants: T[], ask: FacetSource): T[] {
  const eq = (a?: string | null, b?: string | null) => {
    const an = norm(a).toLowerCase();
    return !!an && an === norm(b).toLowerCase();
  };
  const score = (variant: T) =>
    (eq(ask.state, variant.state) ? 4 : 0) + (eq(ask.origin, variant.origin) ? 2 : 0) + (eq(ask.brand, variant.brand) ? 1 : 0);
  return variants
    .map((variant, index) => ({ variant, index, score: score(variant) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.variant);
}
